import { BACKOFFICE_SWAL } from "packs/init-backoffice-confirm"

const FORM_SELECTOR = 'form[data-unsaved-warn], form[data-autosave-manual="true"]'

const IGNORED_FIELD_SELECTOR =
  '[type="hidden"], [name="authenticity_token"], [name="utf8"], [name="_method"], [data-unsaved-ignore]'

const TRACKED_FIELD_SELECTOR =
  'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]):not([type="image"]), select, textarea'

const DIRTY_ICON_TOOLTIP = "Pensez à enregistrer 😉"

const DIRTY_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10ZM12 8v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  '<path d="M11.995 16h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  "</svg>"

let allowNextVisit = false
let beforeUnloadBound = false
let navigationBound = false
let fieldDelegationBound = false
let cacheHookBound = false
let dirtyHistoryPushed = false
let leaveConfirmOpen = false
let guardedPageUrl = null
let turbolinksPopStatePatched = false

const trackedForms = () => Array.from(document.querySelectorAll(FORM_SELECTOR))

const submitButtonsFor = (form) => {
  const buttons = Array.from(
    form.querySelectorAll('button[type="submit"], input[type="submit"]')
  )
  if (!form.id) return buttons

  const external = Array.from(
    document.querySelectorAll(
      `button[type="submit"][form="${form.id}"], input[type="submit"][form="${form.id}"]`
    )
  )
  return buttons.concat(external)
}

const fieldsFor = (form) => {
  const inside = Array.from(form.querySelectorAll(TRACKED_FIELD_SELECTOR))
  let external = []
  if (form.id) {
    external = Array.from(
      document.querySelectorAll(
        `input[form="${CSS.escape(form.id)}"], select[form="${CSS.escape(form.id)}"], textarea[form="${CSS.escape(form.id)}"]`
      )
    ).filter((el) => el.matches(TRACKED_FIELD_SELECTOR))
  }

  return inside.concat(external).filter((el) => {
    if (el.matches?.(IGNORED_FIELD_SELECTOR)) return false
    if (el.closest?.("[data-unsaved-ignore]")) return false
    return true
  })
}

const fieldValue = (el) => {
  if (el instanceof HTMLInputElement) {
    if (el.type === "checkbox" || el.type === "radio") {
      return el.checked ? el.value || "1" : ""
    }
    if (el.type === "file") {
      const file = el.files?.[0]
      return file ? `file:${file.name}:${file.size}` : ""
    }
  }
  return el.value ?? ""
}

const dirtyVisualTarget = (el) => {
  const rich = el.closest?.(".backoffice-rich-text")
  if (rich) return rich
  if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) {
    return el.closest?.(".backoffice-field") || el
  }
  return el
}

const ensureDirtyControlWrap = (target) => {
  if (
    target.classList.contains("backoffice-field") ||
    target.classList.contains("backoffice-rich-text")
  ) {
    target.classList.add("backoffice-dirty-control")
    return target
  }
  if (target.parentElement?.classList.contains("backoffice-dirty-control")) {
    return target.parentElement
  }
  const wrap = document.createElement("div")
  wrap.className = "backoffice-dirty-control"
  target.parentNode.insertBefore(wrap, target)
  wrap.appendChild(target)
  return wrap
}

const applyDirtyUi = (target, dirty) => {
  if (!target) return

  target.classList.toggle("is-dirty-field", dirty)
  if (target.getAttribute("data-unsaved-field-tooltip") === "true") {
    target.removeAttribute("data-tooltip")
    target.removeAttribute("data-unsaved-field-tooltip")
  }

  const wrap = ensureDirtyControlWrap(target)
  let icon = wrap.querySelector(":scope > .backoffice-dirty-field-icon")

  if (dirty) {
    if (!icon) {
      icon = document.createElement("span")
      icon.className = "backoffice-dirty-field-icon"
      icon.setAttribute("data-tooltip", DIRTY_ICON_TOOLTIP)
      icon.setAttribute("role", "img")
      icon.setAttribute("aria-label", DIRTY_ICON_TOOLTIP)
      icon.innerHTML = DIRTY_ICON_SVG
      wrap.appendChild(icon)
    }
  } else if (icon) {
    icon.remove()
  }
}

const setFieldDirtyUi = (el, dirty) => {
  applyDirtyUi(dirtyVisualTarget(el), dirty)
}

const clearFieldDirtyUi = (form) => {
  fieldsFor(form).forEach((el) => setFieldDirtyUi(el, false))
  form.querySelectorAll(".is-dirty-field").forEach((el) => {
    applyDirtyUi(el, false)
  })
  form.querySelectorAll(".backoffice-dirty-field-icon").forEach((el) => el.remove())
  if (form.id) {
    document
      .querySelectorAll(`[form="${CSS.escape(form.id)}"].is-dirty-field, .is-dirty-field[form="${CSS.escape(form.id)}"]`)
      .forEach((el) => applyDirtyUi(el, false))
  }
}

const serializeForm = (form) => {
  const data = new FormData(form)
  const parts = []

  data.forEach((value, key) => {
    if (value instanceof File) {
      if (!value.name && value.size === 0) return
      parts.push(`${key}=file:${value.name}:${value.size}`)
    } else {
      parts.push(`${key}=${value}`)
    }
  })

  return parts.sort().join("\n")
}

const setFormDirty = (form, dirty) => {
  if (!form) return
  form.classList.toggle("is-dirty", dirty)
  form.dataset.unsavedDirty = dirty ? "true" : "false"
  submitButtonsFor(form).forEach((button) => {
    button.classList.toggle("is-dirty-save", dirty)
  })
  if (!dirty) clearFieldDirtyUi(form)
  syncDirtyHistory()
}

const takeSnapshot = (form) => {
  if (!form) return
  form.dataset.unsavedSnapshot = serializeForm(form)
  fieldsFor(form).forEach((el) => {
    el.dataset.unsavedBaseline = fieldValue(el)
  })
}

const syncFieldDirtyState = (el, form) => {
  const related =
    el instanceof HTMLInputElement && el.type === "radio" && el.name
      ? fieldsFor(form).filter(
          (field) => field instanceof HTMLInputElement && field.type === "radio" && field.name === el.name
        )
      : [el]

  related.forEach((field) => {
    if (field.dataset.unsavedBaseline == null) {
      field.dataset.unsavedBaseline = fieldValue(field)
    }
  })

  // Agrège par cible visuelle (ex. plusieurs radios dans le même `.backoffice-field`).
  const dirtyByTarget = new Map()
  related.forEach((field) => {
    const target = dirtyVisualTarget(field)
    if (!target) return
    const dirty = fieldValue(field) !== field.dataset.unsavedBaseline
    dirtyByTarget.set(target, Boolean(dirtyByTarget.get(target)) || dirty)
  })
  dirtyByTarget.forEach((dirty, target) => {
    applyDirtyUi(target, dirty)
  })

  const hasDirtyField = fieldsFor(form).some((field) => {
    if (field.dataset.unsavedBaseline == null) return false
    return fieldValue(field) !== field.dataset.unsavedBaseline
  })
  setFormDirty(form, hasDirtyField)
}

export const markBackofficeFormClean = (form) => {
  if (!form) return
  delete form.dataset.unsavedSubmitting
  takeSnapshot(form)
  setFormDirty(form, false)
}

export const isBackofficePageDirty = () =>
  trackedForms().some((form) => form.dataset.unsavedDirty === "true")

const clearAllDirty = () => {
  trackedForms().forEach((form) => {
    delete form.dataset.unsavedSubmitting
    takeSnapshot(form)
    form.classList.remove("is-dirty")
    form.dataset.unsavedDirty = "false"
    submitButtonsFor(form).forEach((button) => {
      button.classList.remove("is-dirty-save")
    })
    clearFieldDirtyUi(form)
  })
  dirtyHistoryPushed = false
  guardedPageUrl = null
}

const syncDirtyHistory = () => {
  if (!isBackofficePageDirty()) {
    dirtyHistoryPushed = false
    guardedPageUrl = null
    return
  }

  guardedPageUrl = window.location.href
  if (dirtyHistoryPushed || leaveConfirmOpen) return

  try {
    window.history.pushState({ backofficeUnsavedGuard: true }, "", guardedPageUrl)
    dirtyHistoryPushed = true
  } catch (_) {
    // ignore (file://, security errors)
  }
}

const confirmLeaveUnsaved = () => {
  const title = "Modifications non enregistrées"
  const text =
    "Des changements n’ont pas encore été enregistrés. Si vous quittez maintenant, ils seront perdus."

  if (typeof Swal === "undefined") {
    return Promise.resolve(window.confirm(`${title}\n\n${text}`))
  }

  return Swal.fire({
    ...BACKOFFICE_SWAL,
    title,
    html: text,
    icon: "warning",
    showCancelButton: true,
    allowOutsideClick: false,
    allowEscapeKey: true,
    confirmButtonText: "Quitter sans enregistrer",
    cancelButtonText: "Rester sur la page",
  }).then((result) => result.isConfirmed)
}

export const confirmLeaveIfDirty = async () => {
  if (!isBackofficePageDirty()) return true
  return confirmLeaveUnsaved()
}

/**
 * Navigation déclenchée hors lien (toast de notification, JS maison) : passe par
 * la modale d’avertissement si la page a des modifications non enregistrées.
 */
export const visitWithUnsavedGuard = async (url) => {
  if (!url) return

  if (!isBackofficePageDirty()) {
    if (window.Turbolinks) Turbolinks.visit(url)
    else window.location.href = url
    return
  }

  if (leaveConfirmOpen) return
  leaveConfirmOpen = true

  try {
    const ok = await confirmLeaveUnsaved()
    if (ok) leaveToUrl(url)
  } finally {
    leaveConfirmOpen = false
  }
}

const resolveTrackedControl = (target) => {
  if (!(target instanceof Element)) return null

  let field = target
  if (field instanceof HTMLLabelElement) {
    const forId = field.getAttribute("for")
    const control = forId
      ? document.getElementById(forId)
      : field.querySelector("input, select, textarea")
    if (control) field = control
  }

  if (field.closest?.(".backoffice-rich-text__editor")) {
    const root = field.closest(".backoffice-rich-text")
    const source = root?.querySelector(
      "textarea[data-rich-text], textarea.backoffice-rich-text__source"
    )
    if (source) field = source
  }

  if (
    !(
      field instanceof HTMLInputElement ||
      field instanceof HTMLSelectElement ||
      field instanceof HTMLTextAreaElement
    )
  ) {
    return null
  }
  if (field.matches?.(IGNORED_FIELD_SELECTOR)) return null
  if (field.closest?.("[data-unsaved-ignore]")) return null
  return field
}

const resolveTrackedForm = (field) => {
  if (!(field instanceof Element)) return null

  const form = field.closest?.("form")
  if (form?.matches?.(FORM_SELECTOR)) return form

  const formId = field.getAttribute?.("form")
  if (!formId) return null
  const owned = document.getElementById(formId)
  return owned?.matches?.(FORM_SELECTOR) ? owned : null
}

const onFormSubmit = (event) => {
  const form = event.currentTarget
  if (!(form instanceof HTMLFormElement)) return

  // Formulaires AJAX : le dirty est levé après succès d’enregistrement.
  if (
    form.dataset.autosave === "true" ||
    form.dataset.faqItemForm != null ||
    form.dataset.faqSectionForm != null ||
    form.dataset.partnerLogoRename != null ||
    form.dataset.registrationOptionRename != null
  ) {
    return
  }
  form.dataset.unsavedSubmitting = "true"
  takeSnapshot(form)
  setFormDirty(form, false)
}

/**
 * Bind une seule fois. Ne remet JAMAIS un formulaire dirty à clean
 * (les ré-inits FAQ / turbolinks:load ne doivent pas effacer l’état).
 */
const ensureFormBound = (form) => {
  if (!form || form.dataset.unsavedWarnBound === "true") return
  form.dataset.unsavedWarnBound = "true"
  if (form.dataset.unsavedSnapshot == null) {
    takeSnapshot(form)
  } else {
    fieldsFor(form).forEach((el) => {
      if (el.dataset.unsavedBaseline == null) el.dataset.unsavedBaseline = fieldValue(el)
    })
  }
  if (form.dataset.unsavedDirty !== "true") setFormDirty(form, false)
  form.addEventListener("submit", onFormSubmit)
}

const markDirtyFromEvent = (event) => {
  const control = resolveTrackedControl(event.target)
  if (!control) return
  const form = resolveTrackedForm(control)
  if (!form) return
  ensureFormBound(form)
  if (form.dataset.unsavedSubmitting === "true") return
  syncFieldDirtyState(control, form)
}

const bindFieldDelegation = () => {
  if (fieldDelegationBound) return
  fieldDelegationBound = true

  document.addEventListener("input", markDirtyFromEvent, true)
  document.addEventListener("change", markDirtyFromEvent, true)
  // File inputs / certains selects : file & change couvrent le reste.
  document.addEventListener("paste", markDirtyFromEvent, true)
}

const bindBeforeUnload = () => {
  if (beforeUnloadBound) return
  beforeUnloadBound = true

  window.addEventListener("beforeunload", (event) => {
    if (allowNextVisit || !isBackofficePageDirty()) return
    event.preventDefault()
    event.returnValue = ""
  })
}

const leaveToUrl = (url) => {
  allowNextVisit = true
  clearAllDirty()
  if (typeof Swal !== "undefined") Swal.close()
  if (window.Turbolinks) {
    Turbolinks.visit(url)
  } else {
    window.location.href = url
  }
}

/**
 * Turbolinks restaure le snapshot au popstate SANS passer par
 * turbolinks:before-visit — d’où modale qui s’ouvre puis disparaît.
 * On remplace son listener pour intercepter tant que la page est dirty.
 */
const patchTurbolinksPopState = () => {
  const historyController = window.Turbolinks?.controller?.history
  if (!historyController || turbolinksPopStatePatched) return
  turbolinksPopStatePatched = true

  const original = historyController.onPopState
  removeEventListener("popstate", original, false)

  const wrapped = (event) => {
    if (allowNextVisit) {
      allowNextVisit = false
      return original.call(historyController, event)
    }

    if (!isBackofficePageDirty()) {
      dirtyHistoryPushed = false
      guardedPageUrl = null
      return original.call(historyController, event)
    }

    const destinationUrl = window.location.href
    const stayUrl = guardedPageUrl || destinationUrl

    try {
      window.history.pushState({ backofficeUnsavedGuard: true }, "", stayUrl)
      dirtyHistoryPushed = true
      guardedPageUrl = stayUrl
    } catch (_) {
      // ignore
    }

    try {
      window.Turbolinks?.controller?.currentVisit?.cancel()
    } catch (_) {
      // ignore
    }

    if (leaveConfirmOpen) return
    leaveConfirmOpen = true

    confirmLeaveUnsaved()
      .then((ok) => {
        leaveConfirmOpen = false
        if (!ok) return

        allowNextVisit = true
        clearAllDirty()
        if (typeof Swal !== "undefined") Swal.close()

        if (destinationUrl === stayUrl) {
          window.history.go(-2)
        } else if (window.Turbolinks) {
          Turbolinks.visit(destinationUrl)
        } else {
          window.location.href = destinationUrl
        }
      })
      .catch(() => {
        leaveConfirmOpen = false
      })
  }

  historyController.onPopState = wrapped
  addEventListener("popstate", wrapped, false)
}

const bindNavigationGuards = () => {
  if (navigationBound) return
  navigationBound = true

  patchTurbolinksPopState()

  document.addEventListener("turbolinks:before-visit", (event) => {
    if (allowNextVisit) {
      allowNextVisit = false
      return
    }
    if (!isBackofficePageDirty()) return

    event.preventDefault()
    const url = event.data.url

    if (leaveConfirmOpen) return
    leaveConfirmOpen = true

    confirmLeaveUnsaved()
      .then((ok) => {
        leaveConfirmOpen = false
        if (!ok) {
          syncDirtyHistory()
          return
        }
        leaveToUrl(url)
      })
      .catch(() => {
        leaveConfirmOpen = false
      })
  })

  let allowNextLinkClick = false
  document.addEventListener(
    "click",
    (event) => {
      if (allowNextLinkClick) {
        allowNextLinkClick = false
        return
      }
      if (event.defaultPrevented) return
      if (event.button != null && event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const link = event.target.closest?.("a[href]")
      if (!link || !document.body.contains(link)) return
      if (!isBackofficePageDirty()) return
      if (link.target === "_blank" || link.hasAttribute("download")) return

      const href = link.getAttribute("href")
      if (!href || href.startsWith("#") || href.startsWith("javascript:")) return

      if (window.Turbolinks && !link.hasAttribute("data-method") && link.dataset.remote !== "true") {
        return
      }

      event.preventDefault()
      event.stopImmediatePropagation()

      if (leaveConfirmOpen) return
      leaveConfirmOpen = true

      confirmLeaveUnsaved()
        .then((ok) => {
          leaveConfirmOpen = false
          if (!ok) return
          clearAllDirty()
          allowNextVisit = true
          allowNextLinkClick = true
          link.click()
        })
        .catch(() => {
          leaveConfirmOpen = false
        })
    },
    true
  )
}

const resetBoundFlagsBeforeCache = () => {
  trackedForms().forEach((form) => {
    delete form.dataset.unsavedWarnBound
    delete form.dataset.unsavedSnapshot
    delete form.dataset.unsavedSubmitting
    form.classList.remove("is-dirty")
    form.dataset.unsavedDirty = "false"
    submitButtonsFor(form).forEach((button) => {
      button.classList.remove("is-dirty-save")
    })
    fieldsFor(form).forEach((el) => {
      delete el.dataset.unsavedBaseline
    })
    clearFieldDirtyUi(form)
  })
  dirtyHistoryPushed = false
  guardedPageUrl = null
  leaveConfirmOpen = false
  allowNextVisit = false
}

export const initBackofficeUnsavedWarn = () => {
  if (!document.body?.classList.contains("backoffice-body")) return

  // Évite un allowNextVisit « coincé » qui sauterait la modale au prochain clic.
  allowNextVisit = false
  leaveConfirmOpen = false

  // Important : ne pas rebind / reset les forms déjà suivis sur ce DOM.
  // Les appels répétés (FAQ AJAX, double turbolinks:load) effaçaient le dirty.
  trackedForms().forEach(ensureFormBound)
  bindFieldDelegation()
  bindBeforeUnload()
  bindNavigationGuards()

  if (!cacheHookBound) {
    cacheHookBound = true
    document.addEventListener("turbolinks:before-cache", resetBoundFlagsBeforeCache)
  }
}
